const nlhReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const nlhRevealSelector = [
  '.nlh-hero__content',
  '.nlh-advantage',
  '.nlh-section-heading',
  '.nlh-category-card',
  '.nlh-program__media',
  '.nlh-program__content',
  '.nlh-factory__heading',
  '.nlh-factory__media',
  '.nlh-factory__proof',
  '.nlh-faq__heading',
  '.nlh-faq__item',
  '.nlh-footer__main > *',
].join(',');

let nlhRevealObserver;
const nlhHeroRotators = new WeakMap();
let nlhQuoteCenterObserver;
let nlhQuoteCenterFrame;

function nlhInitializeHeroRotator(rotator) {
  nlhHeroRotators.get(rotator)?.destroy?.();

  const slides = Array.from(rotator.querySelectorAll('[data-nlh-hero-slide]'));
  const dots = Array.from(rotator.querySelectorAll('[data-nlh-hero-dot]'));
  const previous = rotator.querySelector('[data-nlh-hero-prev]');
  const next = rotator.querySelector('[data-nlh-hero-next]');
  const pause = rotator.querySelector('[data-nlh-hero-pause]');
  const autoplay = rotator.dataset.autoplay === 'true' && !nlhReducedMotion.matches;
  const interval = Math.max(Number(rotator.dataset.interval) || 6000, 4000);

  if (slides.length < 2) return;

  let activeIndex = Math.max(slides.findIndex((slide) => slide.classList.contains('is-active')), 0);
  let pausedByUser = false;
  let timer;
  const cleanups = [];

  const show = (index) => {
    activeIndex = (index + slides.length) % slides.length;
    slides.forEach((slide, slideIndex) => {
      const isActive = slideIndex === activeIndex;
      slide.classList.toggle('is-active', isActive);
      slide.setAttribute('aria-hidden', String(!isActive));
    });
    dots.forEach((dot, dotIndex) => {
      const isActive = dotIndex === activeIndex;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-current', String(isActive));
    });
  };

  const stop = () => {
    window.clearInterval(timer);
    timer = undefined;
  };

  const start = () => {
    stop();
    if (!autoplay || pausedByUser || document.hidden) return;
    timer = window.setInterval(() => show(activeIndex + 1), interval);
  };

  const listen = (target, event, handler) => {
    if (!target) return;
    target.addEventListener(event, handler);
    cleanups.push(() => target.removeEventListener(event, handler));
  };

  listen(previous, 'click', () => {
    show(activeIndex - 1);
    start();
  });
  listen(next, 'click', () => {
    show(activeIndex + 1);
    start();
  });
  dots.forEach((dot, index) => listen(dot, 'click', () => {
    show(index);
    start();
  }));
  listen(pause, 'click', () => {
    pausedByUser = !pausedByUser;
    pause.classList.toggle('is-paused', pausedByUser);
    pause.setAttribute('aria-pressed', String(pausedByUser));
    pause.setAttribute('aria-label', pausedByUser ? 'Resume product rotation' : 'Pause product rotation');
    start();
  });
  listen(rotator, 'pointerenter', stop);
  listen(rotator, 'pointerleave', start);
  listen(rotator, 'focusin', stop);
  listen(rotator, 'focusout', (event) => {
    if (!rotator.contains(event.relatedTarget)) start();
  });
  listen(document, 'visibilitychange', start);

  const editorSelect = (event) => {
    const selectedSlide = rotator.querySelector(`[data-nlh-slide-id="${event.detail.blockId}"]`);
    if (!selectedSlide) return;
    show(Number(selectedSlide.dataset.slideIndex));
    stop();
  };
  listen(document, 'shopify:block:select', editorSelect);
  listen(document, 'shopify:block:deselect', start);

  show(activeIndex);
  start();

  nlhHeroRotators.set(rotator, {
    destroy() {
      stop();
      cleanups.forEach((cleanup) => cleanup());
    },
  });
}

function nlhPrepareReveal(root = document) {
  const elements = root.querySelectorAll(nlhRevealSelector);
  const designMode = document.documentElement.classList.contains('shopify-design-mode');

  elements.forEach((element, index) => {
    element.dataset.nlhReveal = '';
    element.style.setProperty('--nlh-reveal-delay', `${Math.min(index % 6, 5) * 55}ms`);

    if (designMode || nlhReducedMotion.matches || !('IntersectionObserver' in window)) {
      element.classList.add('is-nlh-visible');
      return;
    }

    nlhRevealObserver.observe(element);
  });
}

function nlhSetHeaderState() {
  const header = document.querySelector('#header-component');
  if (!header) return;
  header.classList.toggle('nlh-header-scrolled', window.scrollY > 18);
}

function nlhEnsureQuoteCenterHeaderLink() {
  const headerMenu = document.querySelector('#header-component header-menu');
  if (!headerMenu) return;

  const linkId = 'nlh-guaranteed-quote-link';
  const styleId = 'nlh-guaranteed-quote-link-style';
  const quotePath = '/pages/product-quote-center';

  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      #${linkId} {
        display: none;
        align-items: center;
        align-self: stretch;
        flex: 0 0 auto;
        color: var(--color-foreground);
        font-family: var(--font-body--family);
        font-size: 0.875rem;
        line-height: 1;
        text-decoration: none;
        white-space: nowrap;
      }

      @media screen and (min-width: 750px) {
        #header-component[data-menu-style='menu'] #${linkId} {
          display: flex;
        }
      }

      #header-component header-menu .menu-list__list-item:has(> a[href*='${quotePath}']) {
        display: none !important;
      }
    `;
    document.head.appendChild(style);
  }

  let quoteLink = document.getElementById(linkId);
  if (!quoteLink) {
    quoteLink = document.createElement('a');
    quoteLink.id = linkId;
    quoteLink.dataset.nlhGuaranteedQuoteLink = 'true';
    quoteLink.href = quotePath;
    quoteLink.textContent = '产品报价中心';
  }

  if (quoteLink.previousElementSibling !== headerMenu) {
    headerMenu.insertAdjacentElement('afterend', quoteLink);
  }

  if (window.location.pathname === quotePath) {
    quoteLink.setAttribute('aria-current', 'page');
  } else {
    quoteLink.removeAttribute('aria-current');
  }
}

function nlhScheduleQuoteCenterHeaderLink() {
  if (nlhQuoteCenterFrame) return;
  nlhQuoteCenterFrame = window.requestAnimationFrame(() => {
    nlhQuoteCenterFrame = undefined;
    nlhEnsureQuoteCenterHeaderLink();
  });
}

function nlhObserveQuoteCenterHeaderLink() {
  nlhEnsureQuoteCenterHeaderLink();
  if (nlhQuoteCenterObserver || !document.body) return;

  nlhQuoteCenterObserver = new MutationObserver(nlhScheduleQuoteCenterHeaderLink);
  nlhQuoteCenterObserver.observe(document.body, { childList: true, subtree: true });
}

function nlhInitialize(root = document) {
  if (!nlhRevealObserver) {
    nlhRevealObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-nlh-visible');
        nlhRevealObserver.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
  }

  nlhPrepareReveal(root);
  root.querySelectorAll('[data-nlh-hero-rotator]').forEach(nlhInitializeHeroRotator);
  nlhSetHeaderState();
  nlhObserveQuoteCenterHeaderLink();
}

document.documentElement.classList.add('nlh-motion-ready');

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => nlhInitialize());
} else {
  nlhInitialize();
}

let nlhScrollFrame;
window.addEventListener('scroll', () => {
  if (nlhScrollFrame) return;
  nlhScrollFrame = window.requestAnimationFrame(() => {
    nlhSetHeaderState();
    nlhScrollFrame = undefined;
  });
}, { passive: true });

document.addEventListener('shopify:section:load', (event) => nlhInitialize(event.target));
document.addEventListener('shopify:section:unload', (event) => {
  event.target.querySelectorAll('[data-nlh-hero-rotator]').forEach((rotator) => {
    nlhHeroRotators.get(rotator)?.destroy?.();
    nlhHeroRotators.delete(rotator);
  });
});
